// After an import, what to ask (components/recipes/ImportReviewSheet.tsx). The
// import fills in everything it can by itself: each ingredient's name (the
// household's own when it is sure — lib/catalog.ts knownName), its amount
// (lib/recipe.ts parseIngredient) and its aisle (the household's category
// memory, then keywords). Only what it couldn't settle is put to the member:
// a rename to confirm, a name that might be one the household uses, a line the
// parser had to guess at ("1–2 klyftor", "salt och peppar"), an ingredient no
// one has filed and no keyword knows. A recipe with none of those asks nothing.

import { nameSuggestions } from './catalog'
import { displayAmount } from './recipe'
import type { ImportedIngredient } from './recipeImport'
import type { RecipeIngredient } from './supabase'
import type { Indexed } from './search'

export type { ImportedIngredient }

export interface ReviewRow {
  /** Its place in the saved recipe. */
  index: number
  /** The line as the page wrote it. */
  raw: string
  /** The recipe's own name for it. */
  from: string
  /** The name it was saved under: the household's when the import renamed it. */
  to: string
  /** Names the household uses that it might be — not sure enough to take. */
  maybe: string[]
  /** The amount as read, to correct. */
  amount: string
  /** The parser had to guess at the line: a range, an "or", an "and". */
  unclear: boolean
  /** Where it goes; null = nobody knows yet, so the review asks. */
  categoryId: string | null
}

export function reviewRows(
  ingredients: ImportedIngredient[],
  renamed: { index: number; from: string; to: string }[],
  o: { known: Indexed<{ name: string }>[]; categoryOf: (g: RecipeIngredient) => string | null; locale?: string },
): ReviewRow[] {
  return ingredients.flatMap((g, index): ReviewRow[] => {
    const rename = renamed.find(r => r.index === index)
    const from = rename?.from ?? g.name
    const row: ReviewRow = {
      index,
      raw: g.raw,
      from,
      to: g.name,
      maybe: rename ? [] : nameSuggestions(g.name, o.known, 2, o.locale),
      amount: displayAmount(g.amount, g.unit, o.locale),
      unclear: g.issues.length > 0,
      categoryId: o.categoryOf(g),
    }
    // "mjölk" saved as the list's "Mjölk" is the same word: not worth a question.
    const renamedReally = row.to.toLocaleLowerCase() !== row.from.toLocaleLowerCase()
    const ask = renamedReally || row.maybe.length > 0 || row.unclear || row.categoryId == null
    return ask ? [row] : []
  })
}
